import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { theme, type ThemeConfig } from 'antd';
import type { ThemeTokens } from '../types/api.types';
import { antdTheme, antdDarkTheme } from '../theme/antd-theme';

interface ThemeStore {
  // State
  mode: 'light' | 'dark';
  tokens: ThemeTokens;
  antdConfig: ThemeConfig;
  isLoaded: boolean;

  // Actions
  setTheme: (tokens: ThemeTokens, mode: 'light' | 'dark') => void;
  toggleMode: () => void;
  reset: () => void;
}

const DEFAULT_TOKENS: ThemeTokens = {
  colorPrimary: '#6366F1',
  colorSuccess: '#10B981',
  colorWarning: '#F59E0B',
  colorError: '#EF4444',
  colorInfo: '#3B82F6',
  borderRadius: 12,
  fontSize: 14,
};

/**
 * Create Ant Design config by merging base theme with custom tokens
 */
function createAntdConfig(tokens: ThemeTokens, mode: 'light' | 'dark'): ThemeConfig {
  const baseTheme = mode === 'dark' ? antdDarkTheme : antdTheme;

  return {
    ...baseTheme,
    token: {
      ...baseTheme.token,
      ...tokens,
    },
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
  };
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      // Initial state
      mode: 'light',
      tokens: DEFAULT_TOKENS,
      antdConfig: createAntdConfig(DEFAULT_TOKENS, 'light'),
      isLoaded: false,

      // Actions
      setTheme: (tokens, mode) => {
        const antdConfig = createAntdConfig(tokens, mode);
        set({
          tokens,
          mode,
          antdConfig,
          isLoaded: true,
        });
      },

      toggleMode: () => {
        const { mode, tokens } = get();
        const newMode = mode === 'light' ? 'dark' : 'light';
        const antdConfig = createAntdConfig(tokens, newMode);
        set({ mode: newMode, antdConfig });
      },

      reset: () => {
        const antdConfig = createAntdConfig(DEFAULT_TOKENS, 'light');
        set({
          mode: 'light',
          tokens: DEFAULT_TOKENS,
          antdConfig,
          isLoaded: false,
        });
      },
    }),
    {
      name: 'dms-theme-storage',
      partialize: (state) => ({ mode: state.mode, tokens: state.tokens }),
    }
  )
);

// Selectors
export const selectThemeMode = (state: ThemeStore) => state.mode;
export const selectThemeTokens = (state: ThemeStore) => state.tokens;
export const selectAntdConfig = (state: ThemeStore) => state.antdConfig;
export const selectIsThemeLoaded = (state: ThemeStore) => state.isLoaded;
